import { useLayout } from '@/layout/LayoutProvider';
import Autosuggest from '@storeblocks/autosuggest';
import { SearchItemDescription } from '@storeblocks/autosuggest/lib/ListItem';
import { Autosuggest as RebrandedAutoSuggest } from '@storeblocks/rebrand/autosuggest';
import { Option } from '@storeblocks/select';
import { getText } from '@storebrand-digital/fms-translations';
import { ChangeEvent, useEffect, useState } from 'react';
import { FieldProps, ObjectSchema, StringSchema } from '../../types';
import { countriesList } from './constant';
import { getError } from './helper';

export type TextInputFieldProps = FieldProps<
  ObjectSchema<{
    countryName: StringSchema;
    countryCode: StringSchema;
  }>
>;

const DEFAULT_VALUE = {
  countryName: '',
  countryCode: ''
};

export const DestinationField = ({
  id,
  name,
  schema,
  value,
  onChange,
  errors,
  forceShowError
}: TextInputFieldProps) => {
  const [inputValue, setInputValue] = useState(value);
  const [hasEnteredInput, setHasEnteredInput] = useState(false);
  const { isRebrand } = useLayout();

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const newValue = { ...inputValue, countryName: e.target.value };
    setInputValue(newValue);
    setHasEnteredInput(true);
    onChange({ target: { name, value: newValue } });
  };

  const handleBlur = () => {
    const matchedCountry = countriesList.find(
      country =>
        country.countryName.toLowerCase() ===
        inputValue.countryName!.toLowerCase()
    );

    if (matchedCountry) {
      const updatedValue = {
        countryName: matchedCountry.countryName,
        countryCode: matchedCountry.countryCode
      };
      setInputValue(updatedValue);
      onChange({ target: { name, value: updatedValue } });
    } else if (
      inputValue.countryName &&
      !countriesList
        .map(str => str.countryName.toLocaleLowerCase())
        .includes(inputValue.countryName.toLocaleLowerCase())
    ) {
      const resetValue = DEFAULT_VALUE;
      setInputValue(resetValue);
      onChange({ target: { name, value: resetValue } });
    }
    setHasEnteredInput(false);
  };

  const handleItemSelect = (label: string, item?: SearchItemDescription) => {
    const selectedItem = item
      ? { countryName: (item as Option).label, countryCode: item.value }
      : { ...inputValue, countryName: label };
    setInputValue(selectedItem);
    onChange({ target: { name, value: selectedItem } });
    setHasEnteredInput(false);
  };

  const options: Option[] = countriesList
    .map(country => ({
      label: country.countryName,
      value: country.countryCode
    }))
    .sort((a, b) => a.label.localeCompare(b.label));

  const filterOptions = (searchArray: string, currentInput: string) => {
    if (!hasEnteredInput) {
      return true;
    }
    return searchArray.toLowerCase().includes(currentInput.toLowerCase());
  };

  return isRebrand ? (
    <RebrandedAutoSuggest
      id={id}
      name={name}
      label={schema.title}
      data={options}
      onChange={handleChange}
      value={inputValue.countryName!}
      noItemFoundText={getText('errors.destinationField.noItemFound')}
      onBlur={handleBlur}
      onItemSelect={handleItemSelect}
      filter={filterOptions}
      showMoreItemsButtonText="Vis flere"
    />
  ) : (
    <Autosuggest
      id={id}
      name={name}
      label={schema.title}
      data={options}
      onChange={handleChange}
      value={inputValue.countryName!}
      error={getError({ errors, forceShowError })}
      noItemFoundText={getText('errors.destinationField.noItemFound')}
      onBlur={handleBlur}
      onItemSelect={handleItemSelect}
      filter={filterOptions}
      dropdownEnabled
    />
  );
};
